import { inject, Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { CmsBlockModelPublicResponse, CmsBlockPublicKey } from "libs/cms/src/lib/models/cms.model";
import { CmsService } from "libs/cms/src/lib/services/cms.lib.service";
import { interval, map, Observable, of, switchMap, takeWhile } from "rxjs";
import { clearMaintenanceTime, setMaintenanceTime } from "../store/maintenance.actions";
import { selectMaintenanceTime } from "../store/maintenance.selectors";

@Injectable({
	providedIn: "root",
})
export class MaintenanceService {
	private cmsService = inject(CmsService);
	private store = inject(Store);

	/**
	 * Fetches the maintenance time from the CMS block 'maintenance_time' and stores it in the
	 * maintenance state of the redux store.
	 */
	public fetchAndStoreMaintenanceTime(): void {
		this.cmsService.getPublicCmsBlock(CmsBlockPublicKey.MAINTENANCE_TIME).subscribe({
			next: (cmsBlock: CmsBlockModelPublicResponse) => {
				if (cmsBlock.activated && cmsBlock.content) {
					this.store.dispatch(setMaintenanceTime({ maintenanceTime: cmsBlock.content }));
				} else {
					this.store.dispatch(clearMaintenanceTime());
				}
			},
			error: (_err) => {
				this.store.dispatch(clearMaintenanceTime());
			},
		});
	}

	/**
	 * @returns An observable that emits a string in format 'HH:MM:SS' that updates every second,
	 * if a value in format "yyyy-mm-dd hh:mm:ss" is stored in the local storage
	 */
	public getTimeToMaintenanceCountdown(): Observable<string | null> {
		return this.store.select(selectMaintenanceTime).pipe(
			switchMap((maintenanceTime) => {
				if (!maintenanceTime) {
					return of(null);
				}
				const maintenanceTimeAsDate = new Date(maintenanceTime);
				return interval(1000).pipe(
					map(() => this.toTimeString(maintenanceTimeAsDate)),
					takeWhile((value): value is string => value !== null, true)
				);
			})
		);
	}

	private toTimeString(maintenanceTimeAsDate: Date): string | null {
		const timeInMS = maintenanceTimeAsDate.getTime() - Date.now();
		return timeInMS > 0 ? new Date(timeInMS).toISOString().substring(11, 19) : null;
	}
}
